import DateRangePicker, { DateRange } from '@mui/lab/DateRangePicker';
import { Box, Button, Popover, SxProps, TextField, Theme, Typography } from '@mui/material';
import { ChangeEvent as ReactChangeEvent, useCallback, useEffect, useState } from 'react';
import { TableInstance } from 'react-table';
import { defaultSearchFilters, useQuoteListState } from 'shared/contexts/quotes-summary-context';
import { useDebounce } from 'shared/hooks';
import { useSessionStorage } from 'shared/hooks/use-session-storage';
import { QuotesSummaryItem } from 'shared/types';
import { formatISODate, replaceDashBySlash } from 'shared/utility';

const rootStyles: SxProps<Theme> = theme => ({
  padding: theme.spacing(3),
});

const gridStyles: SxProps<Theme> = theme => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 200px)',
  [theme.breakpoints.only('sm')]: {
    gridTemplateColumns: '200px',
  },
  gap: '24px',
});

const headerStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
};

const dateHeaderStyles: SxProps<Theme> = {
  mt: 2,
  display: 'flex',
  justifyContent: 'space-between',
};

const dateHeaderTextStyles: SxProps<Theme> = {
  fontSize: '14px',
};

const dateHeaderClearStyles: SxProps<Theme> = {
  padding: 0,
};

const dateRangeStyles: SxProps<Theme> = {
  display: 'flex',
};

const dateDividerStyles: SxProps<Theme> = {
  mx: 2,
  display: 'flex',
  alignItems: 'center',
};

interface QuoteTableFiltersProps {
  anchorEl: Element | null;
  onClose(): void;
  show: boolean;
  quotesSummaryTableInstance: TableInstance<QuotesSummaryItem>;
}

export function QuoteTableFilters({ anchorEl, onClose, show, quotesSummaryTableInstance }: QuoteTableFiltersProps) {
  const { setQuoteTableState, formSearchFilters, setFormSearchFilters } = useQuoteListState();

  const { gotoPage } = quotesSummaryTableInstance;

  const transformDateRange = (dateRange: [string, string] | undefined): DateRange<Date> => {
    // verify if array exists and does not have empty strings to convert them into Date types
    if (!!dateRange && !dateRange.some(d => !d)) {
      const dateRangeTransformed = dateRange.map(date => new Date(replaceDashBySlash(date)));
      if (!!dateRangeTransformed) {
        return [dateRangeTransformed[0], dateRangeTransformed[1]];
      }
    }
    return [null, null];
  };

  const [dateSubmitted, setDateSubmitted] = useState<DateRange<Date>>(
    transformDateRange(formSearchFilters.dateSubmitted)
  );
  const [dateApproved, setDateApproved] = useState<DateRange<Date>>(transformDateRange(formSearchFilters.dateApproved));
  const [dateRevised, setDateRevised] = useState<DateRange<Date>>(transformDateRange(formSearchFilters.dateRevised));
  const setSearchFiltersSessionStorage = useSessionStorage('quoteSummaryFilters', defaultSearchFilters)[1];

  const handleSearchChanged = useCallback(
    (event: ReactChangeEvent<HTMLInputElement>) => {
      const filterColumn = event.target.id;
      const filterValue = event.target.value;
      setFormSearchFilters({
        ...formSearchFilters,
        [filterColumn]: filterValue,
      });
    },
    [setFormSearchFilters, formSearchFilters]
  );

  const debouncedSearch = useDebounce(formSearchFilters, 500);
  useEffect(() => {
    setQuoteTableState(prevState => {
      setSearchFiltersSessionStorage(debouncedSearch);
      return {
        ...prevState,
        searchFilters: debouncedSearch,
        tableState: {
          ...prevState.tableState,
          pageIndex: 0,
        },
      };
    });
    gotoPage(0);
  }, [debouncedSearch, setQuoteTableState, gotoPage, setSearchFiltersSessionStorage]);

  useEffect(() => {
    setFormSearchFilters(formSearchFilters);
    setDateSubmitted(transformDateRange(formSearchFilters.dateSubmitted));
    setDateApproved(transformDateRange(formSearchFilters.dateApproved));
    setDateRevised(transformDateRange(formSearchFilters.dateRevised));
  }, [formSearchFilters, setFormSearchFilters]);

  const handleSearchDateChanged = (filterColumn: string, filterValue: Array<Date | null>) => {
    setQuoteTableState(prevState => {
      const searchFilters = {
        ...prevState.searchFilters,
        [filterColumn]:
          !!filterValue[0] && !!filterValue[1]
            ? [formatISODate(filterValue[0]), formatISODate(filterValue[1])]
            : ['', ''],
      };
      setSearchFiltersSessionStorage(searchFilters);
      return {
        ...prevState,
        searchFilters,
        tableState: {
          ...prevState.tableState,
          pageIndex: 0,
        },
      };
    });
    gotoPage(0);
  };

  return (
    <Popover id='popover-filters' onClose={onClose} open={show} anchorEl={anchorEl}>
      <Box sx={rootStyles}>
        <Box sx={headerStyles}>
          <Typography>Filters</Typography>
        </Box>
        <Box sx={gridStyles}>
          <TextField
            onChange={handleSearchChanged}
            id='quoteNumber'
            label='Quote #'
            variant='standard'
            value={formSearchFilters.quoteNumber}
          />
          <TextField
            onChange={handleSearchChanged}
            id='wholesalerCustomerNumber'
            label='Customer'
            variant='standard'
            value={formSearchFilters.wholesalerCustomerNumber}
          />
          <TextField
            onChange={handleSearchChanged}
            id='jobInfoProjectName'
            label='Project Name'
            variant='standard'
            value={formSearchFilters.jobInfoProjectName}
          />
          <TextField
            onChange={handleSearchChanged}
            id='plumberProjectType'
            label='Project Type'
            variant='standard'
            value={formSearchFilters.plumberProjectType}
          />
          <TextField
            onChange={handleSearchChanged}
            id='brand'
            label='Brand'
            variant='standard'
            value={formSearchFilters.brand}
          />
          <TextField
            onChange={handleSearchChanged}
            id='repAgency'
            label='Rep Agency'
            variant='standard'
            value={formSearchFilters.repAgency}
          />
          <TextField
            onChange={handleSearchChanged}
            id='requestedBy'
            label='Rep'
            variant='standard'
            value={formSearchFilters.requestedBy}
          />
          <TextField
            onChange={handleSearchChanged}
            id='wholesaler'
            label='Wholesaler'
            variant='standard'
            value={formSearchFilters.wholesaler}
          />
          <TextField
            onChange={handleSearchChanged}
            id='plumberContractor'
            label='Plumber'
            variant='standard'
            value={formSearchFilters.plumberContractor}
          />
          <TextField
            onChange={handleSearchChanged}
            id='approvedBy'
            label='Approver'
            variant='standard'
            value={formSearchFilters.approvedBy}
          />
          <TextField
            onChange={handleSearchChanged}
            id='revisionApprovedBy'
            label='Revision Approver'
            variant='standard'
            value={formSearchFilters.revisionApprovedBy}
          />
          <TextField
            onChange={handleSearchChanged}
            id='lastModifiedBy'
            label='Last Modified By'
            variant='standard'
            value={formSearchFilters.lastModifiedBy}
          />
          <TextField
            onChange={handleSearchChanged}
            id='jobInfoStateName'
            label='Job State'
            variant='standard'
            value={formSearchFilters.jobInfoStateName}
          />
        </Box>
        <Box>
          <Box sx={dateHeaderStyles}>
            <Typography sx={dateHeaderTextStyles}>Date Submitted</Typography>
            <Button
              sx={dateHeaderClearStyles}
              onClick={() => {
                setDateSubmitted([null, null]);
                handleSearchDateChanged('dateSubmitted', [null, null]);
              }}
            >
              Clear
            </Button>
          </Box>
          <DateRangePicker
            clearable
            calendars={2}
            startText='Start'
            endText='End'
            value={dateSubmitted}
            onChange={newValue => {
              setDateSubmitted(newValue);
              handleSearchDateChanged('dateSubmitted', newValue);
            }}
            renderInput={(startProps, endProps) => (
              <Box sx={dateRangeStyles}>
                <TextField {...startProps} />
                <Box sx={dateDividerStyles}> to </Box>
                <TextField {...endProps} />
              </Box>
            )}
          />
          <Box sx={dateHeaderStyles}>
            <Typography sx={dateHeaderTextStyles}>Date Revised</Typography>
            <Button
              sx={dateHeaderClearStyles}
              onClick={() => {
                setDateRevised([null, null]);
                handleSearchDateChanged('dateRevised', [null, null]);
              }}
            >
              Clear
            </Button>
          </Box>
          <DateRangePicker
            clearable
            calendars={2}
            startText='Start'
            endText='End'
            value={dateRevised}
            onChange={newValue => {
              setDateRevised(newValue);
              handleSearchDateChanged('dateRevised', newValue);
            }}
            renderInput={(startProps, endProps) => (
              <Box sx={dateRangeStyles}>
                <TextField {...startProps} />
                <Box sx={dateDividerStyles}> to </Box>
                <TextField {...endProps} />
              </Box>
            )}
          />
          <Box sx={dateHeaderStyles}>
            <Typography sx={dateHeaderTextStyles}>Date Approved</Typography>
            <Button
              sx={dateHeaderClearStyles}
              onClick={() => {
                setDateApproved([null, null]);
                handleSearchDateChanged('dateApproved', [null, null]);
              }}
            >
              Clear
            </Button>
          </Box>
          <DateRangePicker
            clearable
            calendars={2}
            startText='Start'
            endText='End'
            value={dateApproved}
            onChange={newValue => {
              setDateApproved(newValue);
              handleSearchDateChanged('dateApproved', newValue);
            }}
            renderInput={(startProps, endProps) => (
              <Box sx={dateRangeStyles}>
                <TextField {...startProps} />
                <Box sx={dateDividerStyles}> to </Box>
                <TextField {...endProps} />
              </Box>
            )}
          />
        </Box>
      </Box>
    </Popover>
  );
}
